import { useEffect, useRef, useState } from 'react';
import { middleEllipsis } from '@shared/lib/middle-ellipsis';
import { useFitMiddleEllipsis } from '@shared/lib/use-fit-middle-ellipsis';
import { COPY_FEEDBACK_MS } from '@shared/lib/copy-feedback-duration';
import { cn } from '@shared/lib/utils';
import { CopyFeedbackIcon } from './copy-feedback-icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

const TRAILING_RESERVE_PX = 16;
const HASH_HEAD_CHARS = 6;
const HASH_TAIL_CHARS = 6;

interface CopyableHashProps {
  value: string;
  onCopy: () => boolean | Promise<boolean | void>;
  className?: string;
}

export function CopyableHash({ value, onCopy, className }: CopyableHashProps) {
  const { text: fitted, widthRef } = useFitMiddleEllipsis(value, TRAILING_RESERVE_PX);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const handleCopy = async () => {
    const result = await onCopy();
    if (result === false) {
      return;
    }
    setCopied(true);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  };

  if (!value) {
    return <span className="text-muted-foreground">-</span>;
  }

  const capped = middleEllipsis(value, HASH_HEAD_CHARS, HASH_TAIL_CHARS);
  const text = fitted === value && value.length > capped.length ? capped : fitted;

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={widthRef} className={cn('flex w-full min-w-0 items-center', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex max-w-full min-w-0 items-center gap-1 text-left font-mono text-xs transition-colors duration-fast ease-standard hover:text-primary"
              onClick={() => void handleCopy()}
            >
              <span className="min-w-0 overflow-hidden whitespace-nowrap">{text}</span>
              <CopyFeedbackIcon copied={copied} className="h-3 w-3 text-primary" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-md break-all font-mono">{value}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
